import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Users, Link2, Heart } from "lucide-react";

const SLA_STYLES = {
  high: {
    text: "text-rose-700",
    border: "border-rose-500/40",
    bg: "bg-rose-500/5",
    label: "High SLA risk",
  },
  medium: {
    text: "text-amber-700",
    border: "border-amber-500/40",
    bg: "bg-amber-500/5",
    label: "Medium SLA risk",
  },
  low: {
    text: "text-emerald-700",
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/5",
    label: "Low SLA risk",
  },
  unknown: {
    text: "text-slate-600",
    border: "border-slate-300",
    bg: "bg-slate-50",
    label: "SLA risk unknown",
  },
};

const ROWS = [
  { key: "sla", Icon: ShieldAlert, label: "SLA risk", testId: "accountability-sla" },
  { key: "ola", Icon: Users, label: "OLA owner", testId: "accountability-ola" },
  { key: "uc", Icon: Link2, label: "UC dependency", testId: "accountability-uc" },
  { key: "xla", Icon: Heart, label: "XLA impact", testId: "accountability-xla" },
];

export default function AccountabilityPanel({ accountability }) {
  if (!accountability) return null;
  const slaStyle = SLA_STYLES[accountability.sla_risk_level] || SLA_STYLES.unknown;
  const values = {
    sla: accountability.sla_risk,
    ola: accountability.ola_owner,
    uc: accountability.uc_dependency,
    xla: accountability.xla_impact,
  };

  return (
    <div
      data-testid="accountability-panel"
      className="cre-surface rounded-sm p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="cre-overline text-sky-700">Accountability mapping</div>
          <h3 className="font-heading text-lg font-bold tracking-tight mt-1">
            SLA · OLA · UC · XLA snapshot
          </h3>
          <p className="mt-1 text-xs text-slate-500 max-w-2xl">
            Lightweight rule-based mapping from incident text — surfaces who
            is on the hook and what's at stake. Not a full SLA / OLA / UC /
            XLA engine.
          </p>
        </div>
        <Badge
          variant="outline"
          className={`rounded-sm font-mono text-[10px] uppercase tracking-wider ${slaStyle.border} ${slaStyle.text} ${slaStyle.bg}`}
          data-testid="accountability-sla-badge"
        >
          {slaStyle.label}
        </Badge>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {ROWS.map(({ key, Icon, label, testId }) => (
          <div
            key={key}
            data-testid={testId}
            className={`border rounded-sm p-3.5 flex gap-3 items-start ${
              key === "sla"
                ? `${slaStyle.border} ${slaStyle.bg}`
                : "border-slate-200 bg-slate-50/60"
            }`}
          >
            <Icon
              className={`h-4 w-4 shrink-0 mt-0.5 ${
                key === "sla" ? slaStyle.text : "text-slate-500"
              }`}
              strokeWidth={1.75}
            />
            <div className="min-w-0 flex-1">
              <div className="cre-overline text-slate-500">{label}</div>
              <div className="mt-1 text-sm text-slate-800 leading-snug break-words">
                {values[key] || "—"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {accountability.rationale?.length ? (
        <details className="mt-3 text-[11px] text-slate-500">
          <summary
            data-testid="accountability-rationale-toggle"
            className="cursor-pointer hover:text-slate-700"
          >
            How was this inferred?
          </summary>
          <ul className="mt-2 ml-3 space-y-0.5 list-disc">
            {accountability.rationale.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
